import { useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import Button from '../Button'
import { AscendFormModal } from './configuration-forms/AscendFormModal'
import { CarestackFormModal } from './configuration-forms/CarestackFormModal'
import { CallRailFormModal } from './configuration-forms/CallRailFormModal'
import { ClickFunnelsFormModal } from './configuration-forms/ClickFunnelsFormModal'
import { DniFormModal } from './configuration-forms/DniFormModal'
import { ElevenLabsFormModal } from './configuration-forms/ElevenLabsFormModal'
import { FacebookAdsFormModal } from './configuration-forms/FacebookAdsFormModal'
import { FacebookLeadsFormModal } from './configuration-forms/FacebookLeadsFormModal'
import { GoogleAdsFormModal } from './configuration-forms/GoogleAdsFormModal'
import { JotformFormModal } from './configuration-forms/JotformFormModal'
import { KollaFormModal } from './configuration-forms/KollaFormModal'
import { RingCentralFormModal } from './configuration-forms/RingCentralFormModal'
import { SikkaFormModal } from './configuration-forms/SikkaFormModal'
import { configuredWorkspaceIntegrations, requiresWorkspaceSetup, type IntegrationName } from './workspaceRequirements'
import { openWorkspaceSetupStep } from './workspaceSetupWizard'
import { GravityFormModal } from 'app/components/integrations/configuration-forms/GravityFormModal'
import { TwilioNumbersModal } from 'app/components/integrations/configuration-forms/TwilioNumbersModal'
import { WebflowFormModal } from 'app/components/integrations/configuration-forms/WebflowFormModal'
import { IntegrationCard } from 'app/components/integrations/IntegrationCard'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { closeModal, showModal } from 'app/lib/modal'
import { IntegrationsRow } from 'app/types/integrations'
import type { WorkspaceIntegrationsRow } from 'app/types/integrations'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { log } from 'lib/logging'

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const AddNewIntegrationModal = observer(function AddNewIntegrationModal({
  onIntegrationAdded,
}: {
  onIntegrationAdded: () => void
}) {
  const [integration, setIntegration] = useState<IntegrationsRow[`integration`]>()
  const user = useUser()
  const dniEnabled = useFeatureToggle(`dni`)
  const isOrgUser = useIsOrgUser()
  const workspace = useWorkspace()
  const [workspaceRows, setWorkspaceRows] = useState<WorkspaceIntegrationsRow[] | undefined>(undefined)
  const [checkingWorkspace, setCheckingWorkspace] = useState(false)
  const workspaceFetchRef = useRef<Promise<WorkspaceIntegrationsRow[] | undefined> | undefined>(undefined)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const promise = window
      .fetch(`/api/integrations/workspace-list`, { credentials: `include` })
      .then((res) => res.json())
      .then((data) => (data.results ?? []) as WorkspaceIntegrationsRow[])
      .catch(() => undefined)
    workspaceFetchRef.current = promise
    bg(async () => {
      const rows = await promise
      if (mountedRef.current) setWorkspaceRows(rows)
    })
  }, [])

  const configured = useMemo(
    () => (workspaceRows ? configuredWorkspaceIntegrations(workspaceRows) : undefined),
    [workspaceRows],
  )

  const badgeFor = (id: IntegrationName): `required` | `connected` | undefined => {
    if (!configured || !requiresWorkspaceSetup(id, workspace?.orgId)) return undefined
    return configured.has(id) ? `connected` : `required`
  }

  const onNextClick = async () => {
    if (integration && requiresWorkspaceSetup(integration, workspace?.orgId)) {
      setCheckingWorkspace(true)
      const rows = await workspaceFetchRef.current
      if (!mountedRef.current) return
      setCheckingWorkspace(false)
      if (rows && !configuredWorkspaceIntegrations(rows).has(integration)) {
        closeModal()
        openWorkspaceSetupStep(integration, onIntegrationAdded)
        return
      }
    }
    closeModal()

    switch (integration) {
      case `Google`:
        showModal(
          <GoogleAdsFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Google Ads`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Facebook Ads`:
        showModal(
          <FacebookAdsFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Facebook Ads`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Facebook Leads`:
        showModal(
          <FacebookLeadsFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Facebook Leads`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Jotform`:
        showModal(
          <JotformFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Jotform`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `CallRail`:
        showModal(
          <CallRailFormModal onAdd={onIntegrationAdded} />,
          {
            title: `CallRail`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Sikka`:
        showModal(
          <SikkaFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Sikka`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Carestack`:
        showModal(
          <CarestackFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Carestack`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `RingCentral`:
        showModal(
          <RingCentralFormModal onAdd={onIntegrationAdded} />,
          {
            title: `RingCentral`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Ascend`:
        showModal(
          <AscendFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Ascend`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Kolla`:
        showModal(
          <KollaFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Kolla`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Gravity Forms`:
        showModal(
          <GravityFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Gravity Forms`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Webflow Forms`:
        showModal(
          <WebflowFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Webflow Forms`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `ClickFunnels`:
        showModal(
          <ClickFunnelsFormModal onAdd={onIntegrationAdded} />,
          {
            title: `ClickFunnels`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Twilio`:
        showModal(
          <TwilioNumbersModal onAdd={onIntegrationAdded} />,
          {
            title: `Twilio`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Dynamic Number Insertion`:
        showModal(
          <DniFormModal onAdd={onIntegrationAdded} />,
          {
            title: `Dynamic Number Insertion - DNI`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break

      case `ElevenLabs`:
        showModal(
          <ElevenLabsFormModal onAdd={onIntegrationAdded} />,
          {
            title: `ElevenLabs`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      default:
        log(`Integration not found`)
        break
    }
  }

  return (
    <div className="flex max-h-[80vh] max-w-[80vw] flex-col items-center justify-center overflow-hidden">
      <div
        className={`inline-flex flex-wrap items-center justify-center gap-4 overflow-y-auto bg-surface-muted p-6${checkingWorkspace ? ` pointer-events-none` : ``}`}
      >
        {isOrgUser && (
          <>
            <IntegrationCard
              name="Ascend"
              id="Ascend"
              icon="/public/ascend.png"
              onClick={() => setIntegration(`Ascend`)}
              currentSelection={integration}
              workspaceBadge={badgeFor(`Ascend`)}
            />
            <IntegrationCard
              name="Jotform"
              id="Jotform"
              icon="/public/jotform.png"
              onClick={() => setIntegration(`Jotform`)}
              currentSelection={integration}
              matIcon
            />
            <IntegrationCard
              name="CallRail"
              id="CallRail"
              icon="/public/callrail.png"
              onClick={() => setIntegration(`CallRail`)}
              currentSelection={integration}
              workspaceBadge={badgeFor(`CallRail`)}
            />
            <IntegrationCard
              name="Sikka"
              id="Sikka"
              icon="/public/sikka_square_logo.png"
              onClick={() => setIntegration(`Sikka`)}
              currentSelection={integration}
              matIcon
            />
            <IntegrationCard
              name="Google Ads"
              id="Google"
              icon="/public/google_ads.png"
              onClick={() => setIntegration(`Google`)}
              currentSelection={integration}
            />
            <IntegrationCard
              name="Carestack"
              id="Carestack"
              icon="/public/carestack.png"
              onClick={() => setIntegration(`Carestack`)}
              currentSelection={integration}
              workspaceBadge={badgeFor(`Carestack`)}
            />
            <IntegrationCard
              name="RingCentral"
              id="RingCentral"
              icon="/public/ringcentral.png"
              onClick={() => setIntegration(`RingCentral`)}
              currentSelection={integration}
              workspaceBadge={badgeFor(`RingCentral`)}
            />
            <IntegrationCard
              name="Kolla"
              id="Kolla"
              icon="/public/kolla.png"
              onClick={() => setIntegration(`Kolla`)}
              currentSelection={integration}
              matIcon
            />
            <IntegrationCard
              name="Gravity Forms"
              id="Gravity Forms"
              icon="/public/gravity.png"
              onClick={() => setIntegration(`Gravity Forms`)}
              currentSelection={integration}
              matIcon
            />
            <IntegrationCard
              name="Webflow Forms"
              id="Webflow Forms"
              icon="/public/webflow.png"
              onClick={() => setIntegration(`Webflow Forms`)}
              currentSelection={integration}
              matIcon
            />
            <IntegrationCard
              name="ClickFunnels"
              id="ClickFunnels"
              icon="/public/clickfunnels.svg"
              onClick={() => setIntegration(`ClickFunnels`)}
              currentSelection={integration}
            />
            <IntegrationCard
              name="Twilio"
              id="Twilio"
              icon="/public/twilio.png"
              onClick={() => setIntegration(`Twilio`)}
              currentSelection={integration}
              workspaceBadge={badgeFor(`Twilio`)}
            />
          </>
        )}
        <IntegrationCard
          name="Facebook Ads"
          id="Facebook Ads"
          icon="/public/meta_icon.png"
          onClick={() => setIntegration(`Facebook Ads`)}
          currentSelection={integration}
          workspaceBadge={badgeFor(`Facebook Ads`)}
        />
        <IntegrationCard
          name="Facebook Leads"
          id="Facebook Leads"
          icon="/public/meta_icon.png"
          onClick={() => setIntegration(`Facebook Leads`)}
          currentSelection={integration}
          workspaceBadge={badgeFor(`Facebook Leads`)}
        />
        {dniEnabled && (
          <IntegrationCard
            name="Dynamic Number Insertion"
            id="Dynamic Number Insertion"
            icon="/public/trackable.png"
            onClick={() => setIntegration(`Dynamic Number Insertion`)}
            currentSelection={integration}
          />
        )}
        {user?.type === `super` && (
          <IntegrationCard
            name="ElevenLabs"
            id="ElevenLabs"
            icon="/public/elevenlabs.png"
            onClick={() => setIntegration(`ElevenLabs`)}
            currentSelection={integration}
          />
        )}
      </div>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-end gap-2">
          <Button label="Cancel" mode="white" size="md" disabled={checkingWorkspace} onClick={() => closeModal()} />
          <Button
            label="Next"
            mode="primary"
            size="md"
            disabled={!integration}
            loading={checkingWorkspace}
            onClick={() => onNextClick()}
          />
        </div>
      </div>
    </div>
  )
})
